import * as React from "react";
import { cva } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-text-sm tw-font-medium tw-whitespace-nowrap tw-transition-all tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary/50 disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0 [&_svg]:tw-size-4",
  {
    variants: {
      variant: {
        default:
          "tw-bg-primary tw-text-black hover:tw-bg-primary-light",
        destructive:
          "tw-bg-bad tw-text-white hover:tw-bg-bad/90",
        outline:
          "tw-border tw-border-border tw-bg-transparent tw-text-white hover:tw-bg-white/5 hover:tw-border-primary/40",
        secondary:
          "tw-bg-surface-secondary tw-text-white hover:tw-bg-surface-secondary/80",
        ghost:
          "tw-text-white hover:tw-bg-white/5",
        link: "tw-text-primary tw-underline-offset-4 hover:tw-underline",
      },
      size: {
        default: "tw-h-9 tw-px-4 tw-py-2",
        xs: "tw-h-6 tw-gap-1 tw-rounded-md tw-px-2 tw-text-xs",
        sm: "tw-h-8 tw-gap-1.5 tw-rounded-md tw-px-3",
        lg: "tw-h-10 tw-rounded-md tw-px-6",
        icon: "tw-size-9",
        "icon-sm": "tw-size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

// Inline fallbacks for the properties that most often lose the cascade to
// runtime-injected MUI/emotion styles on plain <button> elements, and for
// browsers where the Tailwind utility classes silently fail to apply (same
// class of issue as the flex-gap bug — see rsc-member-portal-design skill).
const BASE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  fontWeight: 500,
  fontFamily: "inherit",
  boxSizing: "border-box",
  cursor: "pointer",
  transition: "background 0.15s, border-color 0.15s, color 0.15s",
};

const SIZE_STYLE = {
  default: { height: 36, padding: "0 16px", borderRadius: 8, fontSize: 13.5 },
  xs: { height: 24, padding: "0 8px", borderRadius: 6, fontSize: 11.5 },
  sm: { height: 32, padding: "0 12px", borderRadius: 6, fontSize: 12.5 },
  lg: { height: 40, padding: "0 24px", borderRadius: 6, fontSize: 14 },
  icon: { width: 36, height: 36, padding: 0, borderRadius: 8 },
  "icon-sm": { width: 32, height: 32, padding: 0, borderRadius: 6 },
};

const VARIANT_STYLE = {
  default: { background: "#00ADB5", color: "#000", border: "none" },
  destructive: { background: "#f27059", color: "#fff", border: "none" },
  outline: { background: "transparent", color: "#fff", border: "1px solid #2a2a2e" },
  secondary: { background: "#1e1e1e", color: "#fff", border: "none" },
  ghost: { background: "transparent", color: "#fff", border: "none" },
  link: { background: "transparent", color: "#00ADB5", border: "none" },
};

const Button = React.forwardRef(
  ({ className, variant = "default", size = "default", asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        style={{
          ...BASE_STYLE,
          ...SIZE_STYLE[size],
          ...VARIANT_STYLE[variant],
          ...style,
        }}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
